import { api } from '@orisini/ui/lib/api'
import type { ProductAccess } from '@orisini/ui/context/AuthContext'

/**
 * Klien Project (tabel `workspaces` di orisini-studio) di orisini.com.
 * Akun media sosial opsional: project tanpa akun hanya menghasilkan konten
 * untuk diunduh.
 *
 * Semua permintaan lewat /api/studio/* — orisini-studio yang memegang tabel
 * workspace dan meneruskan aksi akun ke Worker platform. Browser tidak pernah
 * memegang ID akun platform untuk aksi: yang dikirim selalu ID workspace.
 */

export type ProductCode = 'pinboss' | 'gramium'
export type WorkspaceStatus = 'no_account' | 'connected' | 'agent_offline' | 'needs_attention' | 'paused'
export type LatePolicy = 'post_late' | 'requeue' | 'skip'

export interface PlatformAccount {
  id: string
  label: string
  username: string | null
  avatarUrl: string | null
  timezone: string | null
  status: string
  statusMessage: string | null
  lastSessionCheckAt: number | null
  paused: boolean
  maxPostsPerHour: number
  minGapMinutes: number
  latePolicy: LatePolicy
  lateGraceMinutes: number
  createdAt: number
}

export interface Workspace {
  id: string
  name: string
  /** NULL = project tanpa akun. */
  productCode: ProductCode | null
  platform: string | null
  account: PlatformAccount | null
  agentConnected: boolean
  status: WorkspaceStatus
  statusReason: string | null
  createdAt: number
  updatedAt: number
}

export interface PlatformAccounts {
  productCode: ProductCode
  platform: string
  available: boolean
  agentConnected: boolean
  accounts: (PlatformAccount & { workspaceId: string | null })[]
}

export interface Slot {
  id: string
  dow: number
  minuteOfDay: number
  enabled: boolean
  label: string | null
  /** Pinterest: board per slot. */
  boardId?: string | null
  /** Instagram: format per slot. */
  format?: string | null
}

export interface QueueItem {
  id: string
  workspaceId: string
  workspaceName: string
  productCode: ProductCode
  platform: string
  title: string | null
  detail: string | null
  thumbUrl: string | null
  status: string
  onHold: boolean
  fixedTime: boolean
  scheduledAt: number | null
  problems: string[]
  lastError: string | null
}

/** Angka Dashboard (GET /studio/overview). */
export interface Overview {
  projects: number
  projectsWithAccount: number
  posters: number
  carousels: number
  videos: number
  inProgress: number
  awaitingReview: number
  queued: number
  published: number
  accounts: number
  accountLimit: { used: number; max: number } | null
  accountsPartial: boolean
}

const base = '/studio'

export const accountsApi = {
  workspaces: () => api.get<{ workspaces: Workspace[] }>(`${base}/workspaces`).then((r) => r.workspaces),
  workspace: (id: string) => api.get<{ workspace: Workspace }>(`${base}/workspaces/${id}`).then((r) => r.workspace),
  platformAccounts: () =>
    api.get<{ platforms: PlatformAccounts[]; limit: { used: number; max: number } | null }>(`${base}/accounts`),
  overview: () => api.get<Overview>(`${base}/overview`),
  create: (input: { name: string; productCode?: ProductCode; accountId?: string }) =>
    api.post<{ workspace: Workspace }>(`${base}/workspaces`, input).then((r) => r.workspace),
  connect: (id: string, productCode: ProductCode, accountId: string) =>
    api.put<{ workspace: Workspace }>(`${base}/workspaces/${id}/connection`, { productCode, accountId }).then((r) => r.workspace),
  disconnect: (id: string) =>
    api.delete<{ workspace: Workspace }>(`${base}/workspaces/${id}/connection`).then((r) => r.workspace),
  rename: (id: string, name: string) =>
    api.patch<{ workspace: Workspace }>(`${base}/workspaces/${id}`, { name }).then((r) => r.workspace),
  remove: (id: string) => api.delete<{ ok: true }>(`${base}/workspaces/${id}`),

  // --- Akun platform di balik workspace ---
  updateAccount: (id: string, patch: Partial<Pick<PlatformAccount, 'timezone' | 'maxPostsPerHour' | 'minGapMinutes' | 'latePolicy' | 'lateGraceMinutes'>>) =>
    api.patch<{ account: PlatformAccount }>(`${base}/workspaces/${id}/account`, patch).then((r) => r.account),
  setPaused: (id: string, paused: boolean) =>
    api.post<{ ok: true }>(`${base}/workspaces/${id}/account/${paused ? 'pause' : 'resume'}`, {}),
  slots: (id: string) => api.get<{ slots: Slot[]; timezone: string }>(`${base}/workspaces/${id}/account/slots`),
  saveSlots: (id: string, slots: Omit<Slot, 'id' | 'enabled'>[]) =>
    api.post<{ slots: Slot[]; timezone: string }>(`${base}/workspaces/${id}/account/slots/bulk`, { slots }),

  // --- Antrean ---
  queue: () => api.get<{ items: QueueItem[]; unavailable: string[] }>(`${base}/queue`),
  itemAction: (workspaceId: string, itemId: string, action: 'post-now' | 'hold' | 'unhold' | 'dequeue' | 'retry') =>
    api.post(`${base}/workspaces/${workspaceId}/items/${itemId}/${action}`, {}),
}

/** Platform yang ditawarkan wizard. `productCode` kosong = belum didukung. */
export const PLATFORMS: { label: string; productCode: ProductCode | null; hint: string }[] = [
  { label: 'Pinterest', productCode: 'pinboss', hint: 'Pins and boards' },
  { label: 'Instagram', productCode: 'gramium', hint: 'Posts, carousels and Reels' },
  { label: 'Facebook', productCode: null, hint: 'Coming soon' },
  { label: 'Threads', productCode: null, hint: 'Coming soon' },
  { label: 'TikTok', productCode: null, hint: 'Coming soon' },
]

export const STATUS_LABEL: Record<WorkspaceStatus, string> = {
  no_account: 'No account',
  connected: 'Connected',
  agent_offline: 'Agent offline',
  needs_attention: 'Needs attention',
  paused: 'Paused',
}

/**
 * URL media dari platform bisa relatif terhadap origin Worker platform
 * (`/api/media/<id>/content`). Origin-nya diambil dari katalog produk di
 * /api/me — sumber yang sama dengan tautan produk lain, bukan konstanta.
 * Cookie sesi berlaku di semua subdomain, jadi <img> lintas subdomain tetap
 * membawa sesi.
 */
export function mediaUrl(url: string | null, productCode: ProductCode | null, products: ProductAccess[] | undefined): string | null {
  if (!url) return null
  if (!url.startsWith('/')) return url
  const origin = products?.find((p) => p.code === productCode)?.origin
  return origin ? `${origin.replace(/\/$/, '')}${url}` : null
}

export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function minuteToTime(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

export function timeToMinute(t: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

/** "07:30" di zona waktu browser — 24 jam dengan titik dua (konvensi UI). */
export function formatClock(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
}
